import React from 'react'
import isEqual from 'lodash.isequal'
import { Tabs } from '@softwaredev_cegeka/stix-core'
import BaseSearchableList from 'src/components/SearchableList'
import { AuthApi } from 'src/api/authApi'
import { authenticatedUserStore } from 'src/stores'
import { DetailInfo } from '@softwaredev_cegeka/stix-form'
import { notifierStore } from 'src/stores'

const DEFAULT_TAB = 'tenant_admins'
const TENANT_TAB_MAPPINGS = {
    TENANT_ADMINS_TAB: {
        entityName: 'tenant admin',
        dataField: 'tenant_admins',
        itemId: 'id',
        itemDisplay: item => `${item.first_name} ${item.last_name}`,
        selectDisplay: item => `${item.first_name} ${item.last_name}`,
    },
    CHILD_TENANTS_TAB: {
        entityName: 'child tenant',
        dataField: 'child_tenants',
        itemId: 'id',
        itemDisplay: item => `${item.name}`,
        selectDisplay: item => `${item.name}`,
    },
}

/**
 * Tenant Drawer content
 * @returns {*[]}
 */
export default class TenantDrawerContent extends React.Component {
    state = {
        activeTab: DEFAULT_TAB,
        loading: false,
        tenantInfo: {},
    }

    authClient = new AuthApi(authenticatedUserStore.authToken)

    componentDidUpdate(prevProps, prevState, snapshot) {
        if (!isEqual(prevProps.selectedTenant, this.props.selectedTenant)) {
            this.getTenantData()
            this.getParentTenantData()
            this.getChildTenantsData()
        }
    }

    componentWillUnmount() {
        this.authClient.cancelRequest()
    }

    getTenantData = () => {
        this.setState(
            {
                loading: true,
            },
            () => {
                this.authClient
                    .fetchUsers(
                        1,
                        this.props.history,
                        {
                            user_is_admin: true,
                        },
                        this.props.selectedTenant.id
                    )
                    .then(response => {
                        this.setState({
                            tenantInfo: {
                                ...this.state.tenantInfo,
                                tenant_admins: response?.data?.results,
                            },
                            loading: false,
                        })
                    })
                    .catch(err => {
                        this.setState({
                            loading: false,
                        })
                    })
            }
        )
    }

    getParentTenantData = () => {
        if (this.props.selectedTenant.parent_tenant_id) {
            this.setState(
                {
                    activeTab: DEFAULT_TAB,
                    loading: true,
                },
                () => {
                    this.authClient
                        .fetchTenant(this.props.selectedTenant.parent_tenant_id)
                        .then(response => {
                            this.setState({
                                tenantInfo: {
                                    ...this.state.tenantInfo,
                                    parent_tenant: response?.data,
                                },
                                loading: false,
                            })
                        })
                        .catch(err => {
                            this.setState({
                                loading: false,
                            })
                        })
                }
            )
        } else {
            this.setState({
                tenantInfo: {
                    ...this.state.tenantInfo,
                    parent_tenant: null,
                },
                loading: false,
            })
        }
    }

    getChildTenantsData = () => {
        this.setState(
            {
                loading: true,
            },
            () => {
                this.authClient
                    .fetchChildTenants(this.props.selectedTenant.id)
                    .then(response => {
                        this.setState({
                            tenantInfo: {
                                ...this.state.tenantInfo,
                                child_tenants: response?.data?.results,
                            },
                            loading: false,
                        })
                    })
                    .catch(err => {
                        this.setState({
                            loading: false,
                        })
                    })
            }
        )
    }

    addTenantAsChildTenant = tenantId => {
        return this.authClient
            .setTenantAsChildTenant(tenantId, this.props.selectedTenant.id)
            .then(response => {
                if (response.status === 200 || response.status === 204) {
                    this.getChildTenantsData()
                    notifierStore.setToastInfo(
                        'Tenant successfully set as child tenant',
                        true
                    )
                } else {
                    notifierStore.setToastInfo(
                        'Failed to set the tenant as child tenant',
                        false
                    )
                }
            })
            .then(this.props.updateTenantList())
            .catch(err => {
                notifierStore.setToastInfo(err.response?.data?.error, false)
            })
            .finally(notifierStore.setShowMessage(true))
    }

    unlinkTenantAsChildTenant = tenantId => {
        return this.authClient
            .removeTenantAsChildTenant(tenantId)
            .then(response => {
                if (response.status === 200 || response.status === 204) {
                    this.getChildTenantsData()
                    notifierStore.setToastInfo(
                        'Child tenant successfully unlinked from tenant',
                        true
                    )
                } else {
                    notifierStore.setToastInfo(
                        'Failed to unlink child tenant from the tenant',
                        false
                    )
                }
            })
            .then(this.props.updateTenantList())
            .catch(err => {
                notifierStore.setToastInfo(err.response?.data?.error, false)
            })
            .finally(notifierStore.setShowMessage(true))
    }

    render() {
        const emailDomains = this.props.selectedTenant.tenant_domains
            ?.map(item => item.email_domain)
            .join(',')
        const companies = this.props.selectedTenant?.companies
            ?.map(item => item.company_name)
            .join(',')
        return (
            <>
                <DetailInfo
                    data={[
                        {
                            label: 'Email domains',
                            value: emailDomains ? emailDomains : '-',
                        },
                        {
                            label: 'Companies',
                            value: companies,
                        },
                        ...(this.state.tenantInfo.parent_tenant
                            ? [
                                  {
                                      label: 'Parent Tenant',
                                      value: this.state.tenantInfo.parent_tenant
                                          .name,
                                  },
                              ]
                            : []),
                    ]}
                />
                <Tabs
                    variant="material"
                    activeKey={this.state.activeTab}
                    onSelect={key => this.setState({ activeTab: key })}
                    id="tenant-drawer-content-tabs"
                >
                    <Tabs.Pane eventKey="tenant_admins" title="Tenant admins">
                        <BaseSearchableList
                            data={this.state.tenantInfo}
                            loading={this.state.loading}
                            type={TENANT_TAB_MAPPINGS.TENANT_ADMINS_TAB}
                            removeEntityMethod={
                                this.props.unlinkUserAsTenantAdmin
                            }
                            addEntityMethod={this.props.addUserAsTenantAdmin}
                            confirmationMessage={`Are you sure you want to unlink from tenant ${this.props.selectedTenant.name} the tenant admin`}
                            filteredEntitiesUrl={`/api/auth/tenants/${this.props.selectedTenant?.id}/users`}
                            emptyListMessage={`No ${TENANT_TAB_MAPPINGS.TENANT_ADMINS_TAB.entityName} linked to this tenant.`}
                            sortParam={{ sort: '+first_name' }}
                        />
                    </Tabs.Pane>
                    {this.state.tenantInfo.parent_tenant ? (
                        <></>
                    ) : (
                        <Tabs.Pane
                            eventKey="child_tenants"
                            title="Child tenants"
                        >
                            <BaseSearchableList
                                data={this.state.tenantInfo}
                                loading={this.state.loading}
                                type={TENANT_TAB_MAPPINGS.CHILD_TENANTS_TAB}
                                removeEntityMethod={
                                    this.unlinkTenantAsChildTenant
                                }
                                addEntityMethod={this.addTenantAsChildTenant}
                                confirmationMessage={`from ${this.props.selectedTenant.name}`}
                                filteredEntitiesUrl={`/api/auth/tenants?parent_tenant_id=`}
                                filteredEntitiesFilter={el =>
                                    el.id !== this.props.selectedTenant.id
                                }
                                emptyListMessage={`No ${TENANT_TAB_MAPPINGS.CHILD_TENANTS_TAB.entityName} linked to this tenant.`}
                                sortParam={{ sort: '+name' }}
                            />
                        </Tabs.Pane>
                    )}
                </Tabs>
            </>
        )
    }
}
